import React, { useState, useEffect, useMemo } from 'react';
import {
  FaShieldAlt, FaSearch, FaFilter,
  FaTrashAlt, FaEdit, FaUserGraduate,
  FaMoneyBillWave, FaClock, FaArrowRight, FaInfoCircle,
  FaCalendarAlt, FaSync, FaExclamationTriangle
} from 'react-icons/fa';
import api from '../../../utils/api';
import { useAuth } from '../../../context/AuthContext';
import './AlertLog.css';

/* ─── Helpers ─── */
const formatDate = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    + ' • ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
};

const getTodayStr = () => new Date().toISOString().slice(0, 10);
const getMonthStr = () => getTodayStr().slice(0, 7);
const getYearStr  = () => getTodayStr().slice(0, 4);

const ACTION_LABEL = {
  FEE_AMOUNT_EDITED:        'Fee Amount Edited',
  LATE_FEE_ADJUSTED:        'Late Fee Adjusted',
  LATE_FEE_EDITED:          'Late Fee Edited',
  TRANSACTION_AMOUNT_EDITED: 'Transaction Amount Edited',
  ADVANCE_GIVEN:            'Advance Given',
  ATTENDANCE_EDITED:        'Attendance Edited',
  ATTENDANCE_DELETED:       'Attendance Deleted',
  SALARY_DELETED:           'Salary Deleted',
  STUDENT_SENSITIVE_EDITED: 'Student Sensitive Edited',
  STUDENT_DELETED:          'Student Profile Deleted'
};

/* ─── Main Component ─── */
const AlertLog = () => {
  const { user }  = useAuth();
  const [allColleges, setAllColleges] = useState([]);

  useEffect(() => {
    if (user?.colleges && user.colleges.length > 0) {
      setAllColleges(user.colleges);
    } else {
      api.get('/controlpanel/admin/colleges')
        .then(r => setAllColleges(r.data.data || []))
        .catch(() => {});
    }
  }, [user]);

  const [logs, setLogs]         = useState([]);
  const [loading, setLoading]   = useState(false);

  // Filters
  const [searchTerm, setSearchTerm]         = useState('');
  const [roleFilter, setRoleFilter]         = useState('All');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [collegeFilter, setCollegeFilter]   = useState('');
  const [dateMode, setDateMode]   = useState('all');
  const [dateFilter, setDateFilter]   = useState(getTodayStr());
  const [monthFilter, setMonthFilter] = useState(getMonthStr());
  const [yearFilter, setYearFilter]   = useState(getYearStr());

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = {};
      if (collegeFilter)            params.college  = collegeFilter;
      if (categoryFilter !== 'All') params.category = categoryFilter.toLowerCase();

      if (dateMode === 'today') {
        params.from = dateFilter;
        params.to   = dateFilter;
      } else if (dateMode === 'month') {
        const [y, m] = monthFilter.split('-');
        params.from = `${y}-${m}-01`;
        params.to   = new Date(Number(y), Number(m), 0).toISOString().slice(0, 10);
      } else if (dateMode === 'year') {
        params.from = `${yearFilter}-01-01`;
        params.to   = `${yearFilter}-12-31`;
      }

      const { data } = await api.get('/alert-logs', { params });
      setLogs(data.data || []);
    } catch { setLogs([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchLogs(); }, [collegeFilter, categoryFilter, dateMode, dateFilter, monthFilter, yearFilter]); // eslint-disable-line

  const filteredLogs = useMemo(() => {
    return logs.filter(log => {
      const q = searchTerm.toLowerCase();
      const matchSearch = !searchTerm ||
        (log.userName || '').toLowerCase().includes(q) ||
        (log.target   || '').toLowerCase().includes(q) ||
        (log.details  || '').toLowerCase().includes(q) ||
        (log.action   || '').toLowerCase().includes(q);
      const matchRole = roleFilter === 'All' || (log.userRole || '').toLowerCase() === roleFilter.toLowerCase();
      return matchSearch && matchRole;
    });
  }, [logs, searchTerm, roleFilter]);

  return (
    <div className="alert-log-wrapper">

      {/* HEADER */}
      <div className="alert-log-header">
        <div className="alert-log-header-left">
          <div className="header-icon"><FaShieldAlt /></div>
          <div>
            <h1>Audit Log</h1>
            <p>Track financial changes across all roles.</p>
          </div>
        </div>
        <div className="alert-log-stats">
          <div className="stat-badge total"><FaInfoCircle /> {logs.length} Total</div>
          <button className="al-refresh-btn" onClick={fetchLogs} title="Refresh"><FaSync /></button>
        </div>
      </div>

      {/* DATE FILTER TABS */}
      <div className="al-date-tabs">
        <div className="al-date-tab-btns">
          {['all','today','month','year'].map(m => (
            <button key={m} className={`al-date-tab${dateMode === m ? ' active' : ''}`} onClick={() => setDateMode(m)}>
              {m === 'all' ? 'All Time' : m === 'today' ? 'By Date' : m === 'month' ? 'By Month' : 'By Year'}
            </button>
          ))}
        </div>
        <div className="al-date-inputs">
          {dateMode === 'today' && (
            <input type="date" className="al-date-input" value={dateFilter}
              onChange={e => setDateFilter(e.target.value)} />
          )}
          {dateMode === 'month' && (
            <input type="month" className="al-date-input" value={monthFilter}
              onChange={e => setMonthFilter(e.target.value)} />
          )}
          {dateMode === 'year' && (
            <select className="al-date-input" value={yearFilter} onChange={e => setYearFilter(e.target.value)}>
              {[0,1,2,3].map(i => { const y = String(new Date().getFullYear() - i); return <option key={y} value={y}>{y}</option>; })}
            </select>
          )}
        </div>
      </div>

      {/* FILTERS TOOLBAR */}
      <div className="alert-log-toolbar">
        <div className="toolbar-search">
          <FaSearch className="search-icon" />
          <input type="text" placeholder="Search by user, action, target or details..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        </div>

        <div className="toolbar-filters">
          {allColleges.length > 0 && (
            <div className="filter-group">
              <FaFilter className="filter-icon" />
              <select value={collegeFilter} onChange={e => setCollegeFilter(e.target.value)}>
                <option value="">All Colleges</option>
                {allColleges.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
            </div>
          )}
          <div className="filter-group">
            <FaCalendarAlt className="filter-icon" />
            <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
              <option value="All">All Categories</option>
              <option value="attendance">Attendance</option>
              <option value="advance">Advance Salary</option>
              <option value="fee">Fees</option>
              <option value="salary">Salary</option>
              <option value="student">Students</option>
            </select>
          </div>
          <div className="filter-group">
            <FaFilter className="filter-icon" />
            <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
              <option value="All">All Roles</option>
              <option value="admin">Admin</option>
              <option value="collegeadmin">College Admin</option>
              <option value="clerk">Clerk</option>
            </select>
          </div>
        </div>
      </div>

      {/* LOG LIST */}
      {loading ? (
        <div className="alert-log-empty"><FaSync className="empty-icon spin" /><h3>Loading logs...</h3></div>
      ) : filteredLogs.length > 0 ? (
        <div className="alert-log-list">
          {filteredLogs.map((log) => (
            <div key={log._id} className="alert-log-card">
              <div className="log-card-content">

                <div className="log-meta">
                  <span className="log-college">{log.college?.name || '—'}</span>
                  <span className="log-time"><FaClock /> {formatDate(log.createdAt)}</span>
                </div>

                <div className="log-main">
                  <div className="log-user">
                    <span className="user-name">{log.userName}</span>
                    <span className={`role-badge role-${(log.userRole||'').replace(' ','').toLowerCase()}`}>
                      {log.userRole}
                    </span>
                  </div>

                  <div className="log-action-arrow"><FaArrowRight /></div>

                  <div className="log-action-details">
                    <div className="action-title">
                      <span className="action-icon">
                        {log.action?.includes('DELETE') || log.action?.includes('DELETED') ? <FaTrashAlt /> :
                         log.action?.includes('ATTENDANCE') ? <FaCalendarAlt /> :
                         log.action?.includes('STUDENT') ? <FaUserGraduate /> :
                         log.action === 'LATE_FEE_ADJUSTED' || log.action === 'LATE_FEE_EDITED' ? <FaExclamationTriangle style={{color:'#d97706'}} /> :
                         log.action?.includes('EDIT') ? <FaEdit style={{color:'#f59e0b'}} /> :
                         <FaMoneyBillWave />}
                      </span>
                      <strong>{ACTION_LABEL[log.action] || log.action?.replace(/_/g,' ')}</strong>
                    </div>
                    <div className="target-text"><strong>Target:</strong> {log.target}</div>
                  </div>
                </div>

                <div className="log-details-box">
                  {log.details?.split(' | ').map((part, i) => (
                    <p key={i} style={{ margin: '2px 0', color: part.startsWith('Reason:') ? '#7c3aed' : undefined, fontStyle: part.startsWith('Reason:') ? 'italic' : undefined }}>
                      {part}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="alert-log-empty">
          <FaShieldAlt className="empty-icon" />
          <h3>No audit logs found</h3>
          <p>Try adjusting your filters or date range.</p>
        </div>
      )}
    </div>
  );
};

export default AlertLog;